/**
 * ADORACARE-PATIENT-MASTER-B2 — pure helpers for the Patient Master
 * front-desk surface (`/dashboard/patients`).
 *
 * Pure by construction — no React, no fetch, no Supabase, no
 * `Date.now()` (callers inject `nowMs`) — the leads-workspace-view.ts
 * doctrine, so every predicate and validator unit-tests without jsdom.
 *
 * SECURITY POSTURE: nothing in this module is an access decision. The
 * access helpers below only read the SERVER-computed
 * `me.patientMasterEnabled` / `me.patientMasterMergeEnabled` signals
 * (absent = disabled, fail-closed); backend route auth remains the
 * security boundary.
 *
 * CONTRACT MIRRORS (separate-packages convention, kept in lockstep by
 * review): the wire types below mirror api/routes/patient-master.ts;
 * MINOR_AGE_YEARS mirrors PATIENT_MASTER_MINOR_AGE_YEARS.
 *
 * IDENTITY DOCTRINE (ADR-0013): phone is a search key, never identity.
 * Many patients on one phone is a NORMAL outcome the UI must present as
 * a family case, never as an error.
 */

import { isClinicDate } from '@/dashboard/ui/day-scope';
import type { DashboardMe } from '@/dashboard/types';
import type { Lead360PhoneAvailability } from '@/dashboard/owner/lead-360-view';
import {
  parseOriginToken,
  resolveOriginDoor,
  withOrigin,
  type OriginDoor,
} from '@/dashboard/return-to-origin';

// ─── Wire contract mirrors (api/routes/patient-master.ts) ────────────

export type PatientSearchMode = 'phone' | 'umr' | 'name';

export type PatientGender = 'male' | 'female' | 'other' | 'unknown';

export interface PatientCandidateCard {
  readonly contactDisplay?: string;
  readonly contactExpiresAt?: string;
  readonly patientId: string;
  readonly umr: string;
  readonly fullName: string;
  readonly gender: PatientGender;
  readonly ageYears: number | null;
  /** F-3 (PATIENT-360-AND-JOURNEY-TRUTH-CORRECTIVE-V1): TRUE when the age is
   *  the stored approximate (no derivable DOB) -- the surface labels it
   *  "approx.". OPTIONAL so pre-corrective payloads/fixtures keep rendering
   *  (absent = not approximate). Backend derives it via derivePatientAgeView
   *  so Quick View and Patient 360 can never disagree. */
  readonly ageIsApproximate?: boolean;
  readonly city: string | null;
  readonly registeredClinicId: number | null;
  readonly registeredClinicLabel: string | null;
  /** F-3 timezone corrective: the registered clinic's IANA timezone for TRUE
   *  clinic-local registered-date formatting (optional; absent -> default). */
  readonly registeredClinicTimezone?: string | null;
  readonly status: string;
  readonly createdAt: string;
}

/** RECEPTION-FIX-AND-POLISH-BATCH-V1 (FD-15): the desk card facts served
 *  per candidate (keyed by patientId; capped server-side). Every field is
 *  an honest optional -- an absent key or an older wire renders the Tier-0
 *  card unchanged (LES-057 tolerant read). */
export interface PatientCardOutstandingEpisode {
  readonly appointmentTimeIso: string;
  readonly outstandingMinorUnits: number | null;
  readonly notConfigured: boolean;
}

export interface PatientCardDeskFacts {
  readonly visitHistoryAvailability?: 'complete' | 'partial' | 'unavailable';
  readonly lastVisitAt: string | null;
  readonly lastVisitDoctor: string | null;
  readonly lastVisitTreatment: string | null;
  readonly lastVisitTreatmentMoreCount: number;
  readonly nextAppointmentAt: string | null;
  readonly outstandingEpisodes: readonly PatientCardOutstandingEpisode[];
  readonly outstandingEpisodesTruncated: boolean;
}

/** FD-16 (RECEPTION LAW 1): an open, unlinked enquiry surfaced when a
 *  search matched NO patient. Tier-0 administrative facts only -- never a
 *  phone; `source` is the controlled token the display layer humanizes. */
export interface EnquiryGlanceCard {
  readonly contactDisplay?: string;
  readonly contactExpiresAt?: string;
  readonly leadId: number;
  readonly name: string | null;
  readonly city: string | null;
  readonly enquiredAt: string | null;
  readonly source: string;
  readonly clinicName: string | null;
  readonly hasPhone?: boolean;
  readonly registrationStatus?: 'registered' | 'unregistered';
  readonly appointmentPresence?: 'none' | 'recorded' | 'unavailable';
  readonly treatmentInterest?: string | null;
}

export interface PatientSearchResponse {
  readonly total?: number;
  readonly page?: number;
  readonly pageSize?: number;
  readonly complete?: boolean;
  readonly searchMode: PatientSearchMode;
  readonly count: number;
  readonly candidates: readonly PatientCandidateCard[];
  /** Optional on the wire (older payloads / degraded derivations). */
  readonly deskFacts?: Readonly<Record<string, PatientCardDeskFacts>>;
  readonly enquiries?: readonly EnquiryGlanceCard[];
  /** PDC-FO-GAP-CLOSURE-V1 (G1): the ONE in-scope appointment an
   *  appointment-id-shaped query resolved -- present ONLY for that query
   *  shape; null = the id resolved to nothing lawful (out of scope reads
   *  exactly like not-found). Admin facts only. */
  readonly appointmentMatch?: PatientSearchAppointmentMatch | null;
  readonly leadNameLimitReached?: boolean;
}

/** PDC-FO-GAP-CLOSURE-V1 (G1): mirror of the server appointment card. */
export interface PatientSearchAppointmentMatch {
  readonly appointmentId: number;
  readonly clinicId: number;
  readonly clinicLabel: string | null;
  readonly appointmentTimeIso: string;
  readonly status: string;
  readonly patientLabel: string | null;
  /** SHIPPED-WORK-TRUTH-CORRECTIVE-V1 (T3): the appointment's doctor
   *  (roster name; admin staffing fact). Optional on the wire (older
   *  payloads) -- absent/null renders no doctor line (honest absence). */
  readonly doctorLabel?: string | null;
}

export interface CreatePatientPayload {
  relationTitle?: string;
  addressDoor?: string;
  addressStreet?: string;
  addressLandmark?: string;
  addressTaluka?: string;
  addressCountry?: string;

  readonly requestId?: string;
  fullName: string;
  mobile: string;
  gender: PatientGender;
  dob?: string;
  ageYears?: number;
  city?: string;
  registeredClinicId?: number;
  guardian?: { name: string; relationship: string };
  consent?: { grantedBy: string; channel: string };
  /** RECEPTION-OS-BUILD-V1 (Q-12=A / Q-09=D): config-gated registration
   *  fields -- sent only when the org's config renders them AND a value
   *  was typed/picked (a pre-apply database never receives one). */
  relationName?: string;
  occupation?: string;
  handlingCategory?: string;
  /** RECEPTION-FIX-AND-POLISH-BATCH-V1 (FD-19): the optional old EHNote MR
   *  -- lands in patient_identifiers (system 'ehnote_mr'), never a column. */
  externalMr?: string;
  /** REGISTRATION-PARTHA-CONTRACT-V1 (ONE-REGISTRATION-MODULE full parity): the
   *  flat-scalar contract createPatientMasterPatient persists (validated through
   *  THE ONE shared parsers; alt phone -> patient_contact_points). Sent only
   *  when captured. Mirror of the server CreatePatientBody. */
  nameTitle?: string;
  lastName?: string;
  addressArea?: string;
  state?: string;
  pincode?: string;
  altMobile?: string;
  altMobileRelation?: string;
  altMobileRelationName?: string;
  emergencyContactName?: string;
  emergencyContactRelation?: string;
  emergencyContactPhone?: string;
  /** PDC-FO-GAP-CLOSURE-V1 (G2): optional email -> golden-record contact
   *  point (patient_contact_points 'email' via THE ONE builder). */
  email?: string;
}

export type CreatePatientResponse =
  | {
      readonly outcome: 'created';
      readonly patient: PatientCandidateCard;
      readonly sharedPhonePatientCount: number;
      readonly mergeProposalsQueued: number;
    }
  | {
      readonly outcome: 'duplicate_match';
      readonly candidates: readonly PatientCandidateCard[];
    };

export interface PatientRecordResponse {
  readonly patient: PatientCandidateCard & {
    readonly dob: string | null;
    readonly mergedIntoPatientId: string | null;
  };
  readonly identifiers: ReadonlyArray<{
    readonly system: string;
    readonly value: string;
  }>;
  /**
   * Patient contact points -- MASKED BY DEFAULT
   * (PATIENT-CONTACT-PRIVACY-AND-AUDITED-REVEAL-V1, gap.f-25). The raw value
   * NEVER crosses this read: only presence (`hasValue`) and the surrogate
   * `id` the audited reveal targets. The plaintext is fetched solely via
   * `revealPatientContact` (the CONTACT_REVEAL audited path).
   */
  readonly contactPoints: ReadonlyArray<{
    readonly id: number;
    readonly type: string;
    readonly hasValue: boolean;
    readonly isPrimary: boolean;
    readonly doNotContact: boolean;
  }>;
  readonly relatedPersons: ReadonlyArray<{
    readonly name: string | null;
    readonly relatedPatientId: string | null;
    readonly relationship: string;
    readonly isPrimaryGuardian: boolean;
  }>;
  readonly consents: ReadonlyArray<{
    readonly consentType: string;
    readonly grantedBy: string;
    readonly channel: string;
    readonly grantedAt: string;
  }>;
}

export interface MergeQueueItem {
  readonly queueId: number;
  readonly matchScore: number;
  readonly matchedOn: Record<string, unknown>;
  readonly createdAt: string;
  readonly candidateA: PatientCandidateCard;
  readonly candidateB: PatientCandidateCard;
}

export interface MergeQueueResponse {
  readonly count: number;
  readonly items: readonly MergeQueueItem[];
}

export interface MergeDecisionPayload {
  decision: 'approve' | 'reject';
  survivorPatientId?: string;
}

export interface MergeDecisionResponse {
  readonly queueId: number;
  readonly status: 'merged' | 'rejected';
  readonly survivorPatientId: string | null;
  readonly mergedPatientId: string | null;
}

// ─── Access decisions (mirror decideLeadsWorkspaceAccess) ─────────────

export type PatientMasterAccessDecision =
  | { kind: 'wait' }
  | { kind: 'render' }
  | { kind: 'redirect'; to: string };

/**
 * Search / create / record routes: keyed on the server-computed
 * `me.patientMasterEnabled`. ABSENT means disabled (flag off, or the
 * role is outside the create-surface audience) → redirect to the
 * caller's own homePath, so with the flag off nothing new is reachable
 * and no UI changes.
 */
export function decidePatientMasterAccess(state: {
  loading: boolean;
  error: string | null;
  me: DashboardMe | null;
}): PatientMasterAccessDecision {
  if (state.loading) return { kind: 'wait' };
  if (state.error || !state.me) {
    return { kind: 'redirect', to: '/login' };
  }
  if (state.me.patientMasterEnabled !== true) {
    return { kind: 'redirect', to: state.me.homePath };
  }
  return { kind: 'render' };
}

/**
 * The lead-bridge affordance ("Create patient from this lead" --
 * PATIENT-MASTER-CONVERSION-AND-PAYMENT-LINK-COHERENCE-V1). UX gating
 * ONLY -- the server enforces requirePatientMasterCreateScope + the
 * PATIENT_MASTER flag gate regardless. Keys on the server-computed
 * `me.patientMasterEnabled` signal AND a create-audience role (the
 * mirror of PATIENT_MASTER_CREATE_ROLES, api/routes/patient-master.ts:
 * 111-112 -- the separate-packages mirror convention).
 */
export const LEAD_BRIDGE_CREATE_ROLES: readonly string[] = [
  'receptionist',
  'coc',
  'gm_agm',
  'owner',
];

export function canUseLeadBridge(me: DashboardMe | null): boolean {
  return (
    me !== null &&
    me.patientMasterEnabled === true &&
    LEAD_BRIDGE_CREATE_ROLES.includes(me.role)
  );
}

/** Merge admin routes: keyed on `me.patientMasterMergeEnabled` (coc+). */
export function decidePatientMasterMergeAccess(state: {
  loading: boolean;
  error: string | null;
  me: DashboardMe | null;
}): PatientMasterAccessDecision {
  if (state.loading) return { kind: 'wait' };
  if (state.error || !state.me) {
    return { kind: 'redirect', to: '/login' };
  }
  if (state.me.patientMasterMergeEnabled !== true) {
    return { kind: 'redirect', to: state.me.homePath };
  }
  return { kind: 'render' };
}

// ─── Minor rule (mirror of PATIENT_MASTER_MINOR_AGE_YEARS) ────────────

/**
 * Age of majority for the guardian/consent rule (India today; the
 * server constant is authoritative — this mirror only drives when the
 * guardian section of the form appears).
 */
export const MINOR_AGE_YEARS = 18;

/** Whole-year age from an ISO date at `nowMs`, or null when invalid. */
export function ageFromDobIso(dobIso: string, nowMs: number): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dobIso.trim())) return null;
  const dob = new Date(`${dobIso.trim()}T00:00:00Z`);
  if (Number.isNaN(dob.getTime())) return null;
  const now = new Date(nowMs);
  let age = now.getUTCFullYear() - dob.getUTCFullYear();
  const monthDelta = now.getUTCMonth() - dob.getUTCMonth();
  if (
    monthDelta < 0 ||
    (monthDelta === 0 && now.getUTCDate() < dob.getUTCDate())
  ) {
    age -= 1;
  }
  return age >= 0 && age <= 150 ? age : null;
}

/**
 * True when the (dob, ageYears) the form holds describes a minor. dob
 * wins when both are present — same precedence as the server.
 */
export function isMinorInput(
  dob: string | null,
  ageYears: number | null,
  nowMs: number,
): boolean {
  if (dob !== null && dob.trim() !== '') {
    const age = ageFromDobIso(dob, nowMs);
    if (age !== null) return age < MINOR_AGE_YEARS;
  }
  return ageYears !== null && ageYears < MINOR_AGE_YEARS;
}

// ─── Create-form validation (pure; the server re-validates) ──────────

export interface CreateFormState {
  contactReuse?: string;
  fullName: string;
  mobile: string;
  gender: string;
  dob: string;
  ageYears: string;
  city: string;
  guardianName: string;
  guardianRelationship: string;
  consentGrantedBy: string;
  consentChannel: string;
  /** RECEPTION-OS-BUILD-V1 (Q-12=A / Q-09=D): config-gated fields; empty
   *  strings when the org config does not render them. */
  relationName: string;
  occupation: string;
  handlingCategory: string;
  /** FD-17: the relation PREFIX pick (S/o / D/o / W/o) -- a display-layer
   *  accelerator; the stored value stays the ONE relation_name free text
   *  (prefix + name joined at payload build). Empty = no prefix picked. */
  relationPrefix: string;
  /** FD-19: optional old EHNote MR (external-IDs model). */
  externalMr: string;
  /** REGISTRATION-PARTHA-CONTRACT-V1 (ONE-REGISTRATION-MODULE full parity): the
   *  governed contract fields the shared module captures on this person-only
   *  path. Config-gated at render; the person-only writer persists the
   *  flat-scalar set (name_title/last_name/address_area/state/pincode + the
   *  alt-number owner + the emergency trio; the alt phone -> contact_points). */
  nameTitle: string;
  lastName: string;
  relationTitle: string;
  /** PDC-FO-GAP-CLOSURE-V1 (G2): the optional email (never requirable). */
  email: string;
  altMobile: string;
  altMobileRelation: string;
  altMobileRelationName: string;
  state: string;
  pincode: string;
  addressArea: string;
  addressDoor: string;
  addressStreet: string;
  addressLandmark: string;
  addressTaluka: string;
  addressCountry: string;
  emergencyContactName: string;
  emergencyContactRelation: string;
  emergencyContactPhone: string;
}

export const EMPTY_CREATE_FORM: CreateFormState = {
  fullName: '',
  mobile: '',
  gender: 'unknown',
  dob: '',
  ageYears: '',
  city: '',
  guardianName: '',
  guardianRelationship: 'guardian',
  consentGrantedBy: '',
  consentChannel: 'in_person',
  relationName: '',
  occupation: '',
  handlingCategory: '',
  relationPrefix: '',
  externalMr: '',
  nameTitle: '',
  lastName: '',
  relationTitle: '',
  email: '',
  altMobile: '',
  altMobileRelation: '',
  altMobileRelationName: '',
  state: '',
  pincode: '',
  addressArea: '',
  addressDoor: '',
  addressStreet: '',
  addressLandmark: '',
  addressTaluka: '',
  addressCountry: '',
  emergencyContactName: '',
  emergencyContactRelation: '',
  emergencyContactPhone: '',
};

/** REGISTRATION-FIELD-CONTROLS-V1: the contract's State/Country PREFILL rule
 *  applied to the person-only CreateFormState (the CreateFormState twin of
 *  arrivalAddressDefaultsPatch in register-wizard-model.ts -- two form
 *  shapes, ONE rule). Fills ONLY still-empty fields; a typed value is never
 *  overwritten; an empty configured default seeds nothing. */
export function createFormAddressDefaultsPatch(
  form: Pick<CreateFormState, 'state' | 'addressCountry'>,
  config: { readonly stateDefault?: string; readonly countryDefault?: string },
): Partial<CreateFormState> {
  const patch: Partial<CreateFormState> = {};
  const stateDefault = (config.stateDefault ?? '').trim();
  const countryDefault = (config.countryDefault ?? '').trim();
  if (form.state.trim() === '' && stateDefault !== '') patch.state = stateDefault;
  if (form.addressCountry.trim() === '' && countryDefault !== '') {
    patch.addressCountry = countryDefault;
  }
  return patch;
}

/** FD-17: the relation-prefix picklist (Indian-locale registration
 *  vocabulary: son of / daughter of / wife of; free text stays lawful --
 *  the pick is an accelerator, never a constraint). Stored value = the
 *  joined "<prefix> <name>" in the ONE relation_name field. */
export const RELATION_PREFIX_OPTIONS: ReadonlyArray<{
  value: string;
  label: string;
}> = [
  { value: '', label: 'Relation' },
  { value: 'S/o', label: 'S/o (son of)' },
  { value: 'D/o', label: 'D/o (daughter of)' },
  { value: 'W/o', label: 'W/o (wife of)' },
];

/** The ONE relation_name value: prefix + name joined; either half alone
 *  stands (a bare name, or nothing). */
export function joinRelationName(prefix: string, name: string): string {
  const p = prefix.trim();
  const n = name.trim();
  if (p !== '' && n !== '') return `${p} ${n}`;
  return n !== '' ? n : '';
}

export const GENDER_OPTIONS: ReadonlyArray<{ value: PatientGender; label: string }> = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'other', label: 'Other' },
  { value: 'unknown', label: 'Prefer not to say' },
];

export const RELATIONSHIP_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'guardian', label: 'Guardian' },
  { value: 'parent', label: 'Parent' },
  { value: 'spouse', label: 'Spouse' },
  { value: 'child', label: 'Child' },
  { value: 'other', label: 'Other' },
];

export const CONSENT_CHANNEL_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'in_person', label: 'In person' },
  { value: 'phone', label: 'Phone' },
  { value: 'form', label: 'Form' },
];

/**
 * Loose front-desk mobile plausibility: 10-13 digits after stripping
 * separators. The CANONICAL validation is the server's normalizer
 * (lib/phone-normalize.ts) — this only catches obvious typos before
 * the round trip and must never be stricter than the server.
 */
export function isPlausibleMobile(raw: string): boolean {
  const digits = raw.replace(/[\s\-().+]/g, '');
  return /^\d{10,13}$/.test(digits);
}

/** PDC-FO-GAP-CLOSURE-V1 (G2): client-side email plausibility -- the SAME
 *  advisory-mirror posture as isPlausibleMobile above (client hints; the
 *  server's ONE parseContractEmail / isEmailShaped in
 *  lib/email-fingerprint.ts is the gate). Minimal on purpose: local-part @
 *  domain . tld, no whitespace, length-bounded. */
export function isPlausibleEmail(raw: string): boolean {
  const trimmed = raw.trim();
  if (trimmed.length === 0 || trimmed.length > 254) return false;
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed);
}

export interface CreateFormValidation {
  readonly ok: boolean;
  readonly isMinor: boolean;
  /** Field-keyed messages for inline rendering. */
  readonly errors: Readonly<Record<string, string>>;
}

export function validateCreateForm(
  form: CreateFormState,
  nowMs: number,
): CreateFormValidation {
  const errors: Record<string, string> = {};

  if (form.fullName.trim().length === 0) {
    errors.fullName = 'Name is required';
  }
  if (form.mobile.trim().length === 0) {
    errors.mobile = 'Mobile is required';
  } else if (!isPlausibleMobile(form.mobile)) {
    errors.mobile = 'Enter a valid mobile number';
  }

  // PDC-FO-GAP-CLOSURE-V1 (G2): email is OPTIONAL (never required, R-A) --
  // only a TYPED junk value errors; blank passes untouched.
  if (form.email.trim() !== '' && !isPlausibleEmail(form.email)) {
    errors.email = 'Enter a valid email address';
  }

  const dob = form.dob.trim() === '' ? null : form.dob.trim();
  let ageYears: number | null = null;
  if (form.ageYears.trim() !== '') {
    const n = Number(form.ageYears.trim());
    if (!Number.isInteger(n) || n < 0 || n > 150) {
      errors.ageYears = 'Age must be 0-150';
    } else {
      ageYears = n;
    }
  }
  if (dob !== null && ageFromDobIso(dob, nowMs) === null) {
    errors.dob = 'Date of birth must be a valid past date';
  }
  if (dob === null && form.ageYears.trim() === '') {
    errors.ageYears = errors.ageYears ?? 'Age or date of birth is required';
  }

  const isMinor = isMinorInput(dob, ageYears, nowMs);

  if (isMinor) {
    if (form.guardianName.trim().length === 0) {
      errors.guardianName = 'Guardian name is required for a minor';
    }
    if (form.consentGrantedBy.trim().length === 0) {
      errors.consentGrantedBy =
        'Guardian consent (granted by) is required for a minor';
    }
  }

  return { ok: Object.keys(errors).length === 0, isMinor, errors };
}

/**
 * FD-16 (UX LAW 1): the continue-from-enquiry validation -- THE SAME
 * checks minus the mobile pair, because the phone is ON FILE (copied
 * server-side by the bridge; the request never carries one). Never a
 * second rule set: it delegates to validateCreateForm and drops only the
 * mobile errors.
 */
export function validateBridgeContinueForm(
  form: CreateFormState,
  nowMs: number,
): CreateFormValidation {
  const base = validateCreateForm(form, nowMs);
  const { mobile: _mobile, ...rest } = base.errors;
  return { ok: Object.keys(rest).length === 0, isMinor: base.isMinor, errors: rest };
}

/**
 * RECEPTION-OS-BUILD-V1: THE ONE org-config requiredness check for the
 * CreateFormState hosts (PatientCreatePage + GlobalPatientSearchModal --
 * previously two inline copies; F-01-class duplication removed). The wizard
 * host keeps validateArrivalDetails (its own form shape) -- both consume the
 * SAME resolved required set, so the requiredness truth stays single.
 */
export function registrationConfigErrors(
  form: CreateFormState,
  required: ReadonlySet<string>,
): Readonly<Record<string, string>> {
  const out: Record<string, string> = {};
  if (required.has('city') && form.city.trim() === '') {
    out.city = 'City is needed.';
  }
  if (
    required.has('gender') &&
    (form.gender.trim() === '' || form.gender === 'unknown')
  ) {
    out.gender = 'Pick a gender.';
  }
  if (required.has('relation_name') && form.relationName.trim() === '') {
    out.relationName = 'Relation name is needed.';
  }
  if (required.has('occupation') && form.occupation.trim() === '') {
    out.occupation = 'Occupation is needed.';
  }
  return out;
}

/**
 * REGISTRATION-DEPLOYED-WALK-CLOSURE-V1 (Defect C -- EVERY-DOOR REQUIREDNESS
 * PARITY): the canonical required keys still EMPTY in this person-only form.
 * This is the person-only twin of the check-in door's missingRegistrationKeys
 * (patient-intake-mode-view.ts): the SAME requiredness truth, expressed for the
 * CreateFormState shape. Before this, the person-only doors (PatientCreatePage +
 * GlobalPatientSearchModal) RENDERED the config-required contract fields with
 * asterisks yet gated submit on only city/gender/relation_name/occupation
 * (registrationConfigErrors) -- letting an operator mint a Partha patient
 * missing the whole Part C/D set. The LAW trio (full_name / mobile / age_or_dob)
 * stays owned by validateCreateForm's hard errors, so it is NOT repeated here.
 * Returns keys in a stable render order so a needs-line reads down the form.
 */
const CREATE_FORM_VALUE_BY_KEY: Readonly<
  Record<string, (f: CreateFormState) => string>
> = Object.freeze({
  gender: (f) => (f.gender.trim() === '' || f.gender === 'unknown' ? '' : f.gender),
  name_title: (f) => f.nameTitle,
  last_name: (f) => f.lastName,
  relation_title: (f) => f.relationTitle,
  relation_name: (f) => joinRelationName(f.relationPrefix, f.relationName),
  alt_mobile: (f) => f.altMobile,
  alt_mobile_relation: (f) => f.altMobileRelation,
  alt_mobile_relation_name: (f) => f.altMobileRelationName,
  city: (f) => f.city,
  address_area: (f) => f.addressArea,
  state: (f) => f.state,
  pincode: (f) => f.pincode,
  address_door: (f) => f.addressDoor,
  address_street: (f) => f.addressStreet,
  address_landmark: (f) => f.addressLandmark,
  address_taluka: (f) => f.addressTaluka,
  address_country: (f) => f.addressCountry,
  occupation: (f) => f.occupation,
  handling_category: (f) => f.handlingCategory,
  emergency_contact_name: (f) => f.emergencyContactName,
  emergency_contact_relation: (f) => f.emergencyContactRelation,
  emergency_contact_phone: (f) => f.emergencyContactPhone,
});

/** Stable order (identity -> contact -> family -> address -> other ->
 *  emergency), mirroring the registration field plan's group order. */
const CREATE_FORM_REQUIRED_ORDER: readonly string[] = Object.freeze([
  'name_title',
  'last_name',
  'gender',
  'alt_mobile',
  'alt_mobile_relation',
  'alt_mobile_relation_name',
  'relation_title',
  'relation_name',
  'city',
  'address_area',
  'state',
  'pincode',
  'address_door',
  'address_street',
  'address_landmark',
  'address_taluka',
  'address_country',
  'occupation',
  'handling_category',
  'emergency_contact_name',
  'emergency_contact_relation',
  'emergency_contact_phone',
]);

export function missingRequiredCreateKeys(
  form: CreateFormState,
  required: ReadonlySet<string>,
): string[] {
  const out: string[] = [];
  for (const key of CREATE_FORM_REQUIRED_ORDER) {
    if (!required.has(key)) continue;
    const read = CREATE_FORM_VALUE_BY_KEY[key];
    if (read === undefined) continue;
    if (read(form).trim() === '') out.push(key);
  }
  return out;
}

/** Build the POST body from a VALIDATED form (omit empty optionals). */
export function buildCreatePayload(
  form: CreateFormState,
  isMinor: boolean,
  registeredClinicId: number | null,
): CreatePatientPayload {
  const payload: CreatePatientPayload = {
    fullName: form.fullName.trim(),
    mobile: form.mobile.trim(),
    gender: (form.gender as PatientGender) ?? 'unknown',
  };
  if (form.dob.trim() !== '') payload.dob = form.dob.trim();
  if (form.ageYears.trim() !== '') payload.ageYears = Number(form.ageYears.trim());
  if (form.city.trim() !== '') payload.city = form.city.trim();
  const relationJoined = joinRelationName(form.relationPrefix, form.relationName);
  if (relationJoined !== '') payload.relationName = relationJoined;
  if (form.occupation.trim() !== '') payload.occupation = form.occupation.trim();
  if (form.handlingCategory.trim() !== '') {
    payload.handlingCategory = form.handlingCategory.trim();
  }
  if (form.externalMr.trim() !== '') payload.externalMr = form.externalMr.trim();
  // REGISTRATION-PARTHA-CONTRACT-V1 (full parity): the flat-scalar contract the
  // person-only writer persists (name_title/last_name/address_area/state/pincode
  // + the alt-number owner + the emergency trio; the alt phone joins the
  // patient_contact_points identity graph server-side). Validated through THE
  // ONE shared parsers by createPatientMasterPatient.
  if (form.relationTitle.trim() !== '') payload.relationTitle = form.relationTitle.trim();
  if (form.addressDoor.trim() !== '') payload.addressDoor = form.addressDoor.trim();
  if (form.addressStreet.trim() !== '') payload.addressStreet = form.addressStreet.trim();
  if (form.addressLandmark.trim() !== '') payload.addressLandmark = form.addressLandmark.trim();
  if (form.addressTaluka.trim() !== '') payload.addressTaluka = form.addressTaluka.trim();
  if (form.addressCountry.trim() !== '') payload.addressCountry = form.addressCountry.trim();
  if (form.nameTitle.trim() !== '') payload.nameTitle = form.nameTitle.trim();
  if (form.lastName.trim() !== '') payload.lastName = form.lastName.trim();
  if (form.addressArea.trim() !== '') payload.addressArea = form.addressArea.trim();
  if (form.state.trim() !== '') payload.state = form.state.trim();
  if (form.pincode.trim() !== '') payload.pincode = form.pincode.trim();
  // PDC-FO-GAP-CLOSURE-V1 (G2): the optional email rides when given (both
  // person-door flows; THE ONE server parser is the 400 gate).
  if (form.email.trim() !== '') payload.email = form.email.trim();
  if (form.altMobile.trim() !== '') payload.altMobile = form.altMobile.trim();
  if (form.altMobileRelation.trim() !== '') {
    payload.altMobileRelation = form.altMobileRelation.trim();
  }
  if (form.altMobileRelationName.trim() !== '') {
    payload.altMobileRelationName = form.altMobileRelationName.trim();
  }
  if (form.emergencyContactName.trim() !== '') {
    payload.emergencyContactName = form.emergencyContactName.trim();
  }
  if (form.emergencyContactRelation.trim() !== '') {
    payload.emergencyContactRelation = form.emergencyContactRelation.trim();
  }
  if (form.emergencyContactPhone.trim() !== '') {
    payload.emergencyContactPhone = form.emergencyContactPhone.trim();
  }
  if (registeredClinicId !== null) payload.registeredClinicId = registeredClinicId;
  if (isMinor) {
    payload.guardian = {
      name: form.guardianName.trim(),
      relationship: form.guardianRelationship,
    };
    payload.consent = {
      grantedBy: form.consentGrantedBy.trim(),
      channel: form.consentChannel,
    };
  }
  return payload;
}

/**
 * FD-16 (RECEPTION LAW 2 -- CONTINUOUS JOURNEY): the continue-from-enquiry
 * payload for POST /patient-master/lead-bridge. SAME form, SAME field
 * grammar as buildCreatePayload, with ONE structural difference: NO mobile
 * -- the bridge copies the phone SERVER-SIDE from the vault (PHI law; the
 * request never carries a phone). The journey reattaches (lead links),
 * UMR mints exactly once (the bridge's re-match is the gate).
 */
export function buildBridgeContinuePayload(
  form: CreateFormState,
  isMinor: boolean,
  leadId: number,
): {
  leadId: number;
  relationTitle?: string;
  addressDoor?: string;
  addressStreet?: string;
  addressLandmark?: string;
  addressTaluka?: string;
  addressCountry?: string;

  fullName?: string;
  gender?: string;
  dob?: string;
  ageYears?: number;
  city?: string;
  relationName?: string;
  occupation?: string;
  handlingCategory?: string;
  externalMr?: string;
  // REGISTRATION-DEPLOYED-WALK-CLOSURE-V1 (Defect C, no-capture-then-drop): the
  // continue-from-enquiry flow renders the SAME governed module as a fresh
  // registration, so it forwards the SAME contract set. Previously these were
  // captured in the UI and dropped here (this builder + the bridge route both
  // omitted them). THE ONE writer validates + persists each.
  nameTitle?: string;
  lastName?: string;
  addressArea?: string;
  state?: string;
  pincode?: string;
  email?: string;
  altMobile?: string;
  altMobileRelation?: string;
  altMobileRelationName?: string;
  emergencyContactName?: string;
  emergencyContactRelation?: string;
  emergencyContactPhone?: string;
  guardian?: { name: string; relationship: string };
  consent?: { grantedBy: string; channel: string };
} {
  const payload: ReturnType<typeof buildBridgeContinuePayload> = { leadId };
  if (form.fullName.trim() !== '') payload.fullName = form.fullName.trim();
  payload.gender = (form.gender as PatientGender) ?? 'unknown';
  if (form.dob.trim() !== '') payload.dob = form.dob.trim();
  if (form.ageYears.trim() !== '') payload.ageYears = Number(form.ageYears.trim());
  if (form.city.trim() !== '') payload.city = form.city.trim();
  const relationJoined = joinRelationName(form.relationPrefix, form.relationName);
  if (relationJoined !== '') payload.relationName = relationJoined;
  if (form.occupation.trim() !== '') payload.occupation = form.occupation.trim();
  if (form.handlingCategory.trim() !== '') {
    payload.handlingCategory = form.handlingCategory.trim();
  }
  if (form.externalMr.trim() !== '') payload.externalMr = form.externalMr.trim();
  if (form.relationTitle.trim() !== '') payload.relationTitle = form.relationTitle.trim();
  if (form.addressDoor.trim() !== '') payload.addressDoor = form.addressDoor.trim();
  if (form.addressStreet.trim() !== '') payload.addressStreet = form.addressStreet.trim();
  if (form.addressLandmark.trim() !== '') payload.addressLandmark = form.addressLandmark.trim();
  if (form.addressTaluka.trim() !== '') payload.addressTaluka = form.addressTaluka.trim();
  if (form.addressCountry.trim() !== '') payload.addressCountry = form.addressCountry.trim();
  if (form.nameTitle.trim() !== '') payload.nameTitle = form.nameTitle.trim();
  if (form.lastName.trim() !== '') payload.lastName = form.lastName.trim();
  if (form.addressArea.trim() !== '') payload.addressArea = form.addressArea.trim();
  if (form.state.trim() !== '') payload.state = form.state.trim();
  if (form.pincode.trim() !== '') payload.pincode = form.pincode.trim();
  // PDC-FO-GAP-CLOSURE-V1 (G2): the optional email rides when given (both
  // person-door flows; THE ONE server parser is the 400 gate).
  if (form.email.trim() !== '') payload.email = form.email.trim();
  if (form.altMobile.trim() !== '') payload.altMobile = form.altMobile.trim();
  if (form.altMobileRelation.trim() !== '') {
    payload.altMobileRelation = form.altMobileRelation.trim();
  }
  if (form.altMobileRelationName.trim() !== '') {
    payload.altMobileRelationName = form.altMobileRelationName.trim();
  }
  if (form.emergencyContactName.trim() !== '') {
    payload.emergencyContactName = form.emergencyContactName.trim();
  }
  if (form.emergencyContactRelation.trim() !== '') {
    payload.emergencyContactRelation = form.emergencyContactRelation.trim();
  }
  if (form.emergencyContactPhone.trim() !== '') {
    payload.emergencyContactPhone = form.emergencyContactPhone.trim();
  }
  if (isMinor) {
    payload.guardian = {
      name: form.guardianName.trim(),
      relationship: form.guardianRelationship,
    };
    payload.consent = {
      grantedBy: form.consentGrantedBy.trim(),
      channel: form.consentChannel,
    };
  }
  return payload;
}

// ---- FD-16/FD-27: the enquiry-continue vocabulary (desk words only) --------

/** FD-27: the trust line when registration continues an existing enquiry. */
export const ENQUIRY_CONTINUE_TRUST_LINE =
  'Existing enquiry found - continuing registration.';

/** The enquiry glance section header (person-first vocabulary -- the desk
 *  never sees "lead" or an object type; an enquiry is a person's earlier
 *  contact). */
export const ENQUIRY_GLANCE_TITLE = 'No patient record yet - enquiry found';

export const ENQUIRY_CONTINUE_ACTION = 'Continue registration';

/** The mobile-on-file confirmation line for a continue (UX LAW 1: confirm,
 *  never re-collect -- the phone is copied server-side from the enquiry). */
export function enquiryPhoneOnFileLine(typedQuery: string): string {
  const digits = typedQuery.replace(/\D/g, '');
  return digits.length >= 4
    ? `Mobile: on file from this enquiry (ending ${digits.slice(-4)})`
    : 'Mobile: on file from this enquiry';
}

// ---- F-14: the walk-in source question (RECEPTION-OS-BUILD-V1) --------
//
// The question the tenant's own front desk asks first (COC-CSD workflow
// s.1: "ask how the patient heard of Partha Dental") -- lost when the
// unhosted WalkinSourceTiles died (F-13/F-14) and re-homed HERE on the
// surviving register flow. Values are the WALK-IN attribution vocabulary
// (api/routes/walkins.ts SOURCES -- the exact set the owner's "Walk-in
// sources" report reads); labels are plain desk words. Distinct from the
// LEAD source and from the booking channel (three facts, never conflated;
// R-D).

export const WALKIN_SOURCE_QUESTION = 'How did this patient hear about us?';

export const WALKIN_SOURCE_OPTIONS: ReadonlyArray<{
  readonly value: string;
  readonly label: string;
}> = [
  { value: 'walk_in', label: 'Walked in / nearby' },
  { value: 'referral', label: 'Referral' },
  { value: 'paid_search', label: 'Google' },
  { value: 'paid_social', label: 'Social media' },
  { value: 'organic_listing', label: 'Online listing' },
  { value: 'messaging', label: 'WhatsApp' },
  { value: 'phone_call', label: 'Phone call' },
  { value: 'other', label: 'Other' },
];

export const DEFAULT_WALKIN_SOURCE = 'walk_in';

// ─── Display helpers ──────────────────────────────────────────────────

const GENDER_SHORT: Readonly<Record<PatientGender, string>> = {
  female: 'F',
  male: 'M',
  other: 'O',
  unknown: '—',
};

/** Picker cell: "F · 34y" / "M · ~34y (approx.)" / "M · age unknown".
 *  F-3: when the age is the stored approximate (no derivable DOB), it is
 *  EXPLICITLY labeled -- never presented as an exact DOB-derived age. Both
 *  Quick View and Patient 360 call THIS with the same wire fields, so they
 *  render identically. */
export function genderAgeLabel(
  gender: PatientGender,
  ageYears: number | null,
  isApproximate?: boolean,
): string {
  const g = GENDER_SHORT[gender] ?? '—';
  if (ageYears === null) return `${g} · age unknown`;
  return isApproximate === true
    ? `${g} · ~${ageYears}y (approx.)`
    : `${g} · ${ageYears}y`;
}

// ─── Masked contact reveal (PATIENT-CONTACT-PRIVACY-AND-AUDITED-REVEAL-V1) ──

/** The reveal outcome contract -- the SAME shape the canonical reveal control
 *  consumes (RevealOutcomePayload). No second privacy contract. */
export type PatientContactRevealResult =
  | { outcome: 'revealed'; field: string; value: string; rehideSeconds: number }
  | { outcome: 'no_value'; field: string; reason: string };

/**
 * Map a masked contact point's presence to the canonical reveal-control
 * availability vocabulary (@/dashboard/owner/lead-360-view). A stored value
 * -> 'available' (the reveal action shows); absence -> 'no_linked_phone'
 * (designed copy, no dead reveal button). Pure -- unit-tested without jsdom.
 */
export function contactPointAvailability(
  hasValue: boolean,
): Lead360PhoneAvailability {
  return hasValue ? 'available' : 'no_linked_phone';
}

/** Human label for a contact point type ('mobile' -> 'Mobile'). Display-only. */
export function contactPointTypeLabel(type: string): string {
  const t = type.trim();
  if (t.length === 0) return 'Contact';
  return t.charAt(0).toUpperCase() + t.slice(1).replace(/_/g, ' ');
}

/** Record/picker href helpers (mirror workspaceLeadDetailHref shape). */
export function patientRecordHref(patientId: string): string {
  return `/dashboard/patients/${encodeURIComponent(patientId)}`;
}

// --- L-18: where the DESK patient record was reached FROM --------------------
//
// THE DEFECT, in the tenant's own words (LAKSHMI-BATCH-2-INTAKE-V1, L-18):
// "the desk record's back link is a constant to a search-only page". The
// receptionist reaches this record from her day board, from the global search
// modal, from the recently-viewed strip, from the timeline and from a receipt
// -- and the control said "Patient search" and went there from every one of
// them. Third instance of the one missing primitive (L-09 on the doctor lens,
// gap.f-02 on the leads list).
//
// THE PRIMITIVE IS NOT RE-DERIVED HERE. parseOriginToken / withOrigin /
// resolveOriginDoor come from '@/dashboard/return-to-origin', built once for
// L-09; a second implementation would be the defect, not the fix. This module
// supplies only what is genuinely local: the desk's OWN allowlist and its
// origin -> door map.
//
// A TOKEN, NEVER A URL (the primitive's own law): '?from=' is operator-
// editable, so the destination maps an allowlisted token to a known href.
// A forged value cannot become an open redirect -- it resolves to null and
// falls back to the honest default door.

export const DESK_RECORD_ORIGINS = [
  'search',
  'walkin',
  'timeline',
  'billing',
  'money',
  'reports',
  'workboard',
] as const;

export type DeskRecordOrigin = (typeof DESK_RECORD_ORIGINS)[number];

export function parseDeskRecordOrigin(
  raw: string | null | undefined,
): DeskRecordOrigin | null {
  return parseOriginToken(DESK_RECORD_ORIGINS, raw);
}

/** Money's URL carries only its existing read controls. Clinic stays with the
 * shared lens and the server still validates every requested view/filter. */
const MONEY_FILTERS = {
  day_ledger: ['all', 'in', 'out', 'cash_in', 'spent', 'deposits', 'non_cash'],
  expenses: ['all', 'proof_missing'],
  deposits: ['all'],
  collections_by_mode: ['all'],
} as const;

export function moneyDeskContext(search: string | URLSearchParams): URLSearchParams {
  const input = new URLSearchParams(search);
  const result = new URLSearchParams();
  const one = (key: string): string | null => {
    const values = input.getAll(key);
    return values.length === 1 ? values[0] : null;
  };
  const from = one('from');
  const to = one('to');
  if (from !== null && to !== null && isClinicDate(from) && isClinicDate(to) && from <= to) {
    result.set('from', from);
    result.set('to', to);
  }
  const view = one('view');
  if (view !== null && Object.prototype.hasOwnProperty.call(MONEY_FILTERS, view)) {
    result.set('view', view);
    const filter = one('filter');
    if (filter !== null && (MONEY_FILTERS[view as keyof typeof MONEY_FILTERS] as readonly string[]).includes(filter)) {
      result.set('filter', filter);
    }
  }
  return result;
}

/** Only these navigation values travel through the record. The destination
 * remains the existing origin-token map; no arbitrary return URL or form data
 * is accepted. Validate on entry and again when resolving an edited URL. */
function deskRecordContext(
  origin: DeskRecordOrigin | null | undefined,
  search: string | URLSearchParams,
  fromRecord = false,
): URLSearchParams {
  const params = new URLSearchParams(search);
  const result = new URLSearchParams();
  const read = (key: string): string | null => {
    const name = fromRecord ? `back${key[0].toUpperCase()}${key.slice(1)}` : key;
    const values = params.getAll(name);
    return values.length === 1 ? values[0] : null;
  };
  if (origin === 'walkin') {
    const date = read('date');
    const endDate = read('endDate');
    const view = read('view');
    if (date !== null && isClinicDate(date)) {
      result.set('date', date.trim());
    }
    // Home omits date when the range starts today. Preserve that valid
    // URL shape; the existing board remains the owner of day resolution.
    if (endDate !== null && isClinicDate(endDate) &&
        (date === null || (isClinicDate(date) && endDate.trim() >= date.trim()))) {
      result.set('endDate', endDate.trim());
    }
    if (view === 'checked_out' || view === 'everyone') result.set('view', view);
  } else if (origin === 'billing') {
    const id = read('appointmentId');
    if (id !== null && /^[1-9]\d*$/.test(id) && Number.isSafeInteger(Number(id))) {
      result.set('appointmentId', id);
    }
  } else if (origin === 'reports') {
    for (const key of ['clinicId','doctorId'] as const) {
      const value=read(key);
      if(value && /^[1-9]\d*$/.test(value) && Number.isSafeInteger(Number(value))) result.set(key,value);
    }
    for (const key of ['from','to','compareFrom','compareTo'] as const) {
      const value=read(key);
      if(value && isClinicDate(value)) result.set(key,value.trim());
    }
  } else if (origin === 'workboard') {
    const query = read('searchQuery');
    if (query !== null && query.length <= 200) result.set('searchQuery', query);
  } else if (origin === 'money') {
    const current = new URLSearchParams();
    for (const key of ['from', 'to', 'view', 'filter'] as const) {
      const value = read(key);
      if (value !== null) current.set(key, value);
    }
    moneyDeskContext(current).forEach((value, key) => result.set(key, value));
  }
  return result;
}

/** No origin/context leaves existing callers' URLs byte-identical. */
export function deskRecordHref(
  patientId: string,
  origin?: DeskRecordOrigin | null,
  sourceSearch: string | URLSearchParams = '',
): string {
  const href = withOrigin(patientRecordHref(patientId), origin);
  const context = deskRecordContext(origin, sourceSearch);
  const stamped = new URLSearchParams();
  context.forEach((value, key) => stamped.set(`back${key[0].toUpperCase()}${key.slice(1)}`, value));
  return stamped.size === 0 ? href : `${href}&${stamped}`;
}

/** The honest default when nothing was stamped: exactly the constant this
 *  page shipped with, label included, so an unstamped arrival is unchanged. */
export const DESK_RECORD_DEFAULT_DOOR: OriginDoor = {
  href: '/dashboard/patients',
  label: 'Patient search',
};

/**
 * The Back door for the origin actually travelled. The LABEL follows the
 * BEHAVIOUR (the primitive's law): it names the surface the control returns
 * to, so "back" can never say one thing and do another again.
 */
export function deskRecordBackDoor(
  origin: DeskRecordOrigin | null,
  patientId: string | null,
  recordSearch: string | URLSearchParams = '',
): OriginDoor {
  const context = deskRecordContext(origin, recordSearch, true).toString();
  const suffix = context === '' ? '' : `?${context}`;
  return resolveOriginDoor<DeskRecordOrigin>(
    {
      search: () => DESK_RECORD_DEFAULT_DOOR,
      walkin: () => ({ href: `/walkin${suffix}`, label: 'Front desk' }),
      // The timeline door needs the record's OWN id. Without one the origin
      // is known but NOT REACHABLE on this render, so it degrades to the
      // default door -- never a dead door (LES-016, the L-09 precedent).
      timeline: () =>
        patientId === null || patientId === ''
          ? null
          : {
              href: `${patientRecordHref(patientId)}/timeline`,
              label: 'Timeline',
            },
      billing: () => ({ href: `/dashboard/billing-entry${suffix}`, label: 'Billing entry' }),
      money: () => ({ href: `/dashboard/money${suffix}`, label: 'Money' }),
      reports: () => ({ href: `/dashboard/clinic-reports${suffix}`, label: 'Reports' }),
      workboard: () => ({ href: `/dashboard/workboard${suffix}`, label: 'Assigned tasks' }),
    },
    origin,
    DESK_RECORD_DEFAULT_DOOR,
  );
}

/**
 * SEARCH-FIRST STRUCTURAL GATE (PR #414 founder corrective). The register
 * page is reachable ONLY through a search-evidence door, and the door
 * states WHICH evidence sent the operator through (`from=` marker):
 *
 *   no-match        a search ran and returned nothing suitable
 *   none-of-these   results existed and the operator explicitly said no
 *   search-failed   the search SERVICE failed -- registration stays open
 *                   with the honest warning; the server's duplicate-first
 *                   check remains mandatory at submit
 *
 * The marker is a UI/structural gate, NEVER the duplicate-security
 *  boundary -- the server-side duplicate-first check is binding regardless
 * (a forged URL gains nothing the server would not refuse). Direct
 * navigation with no valid marker redirects to patient search.
 */
export const REGISTER_ORIGINS = ['no-match', 'none-of-these', 'search-failed'] as const;

export type RegisterOrigin = (typeof REGISTER_ORIGINS)[number];

export function parseRegisterOrigin(raw: string | null): RegisterOrigin | null {
  return (REGISTER_ORIGINS as readonly string[]).includes(raw ?? '')
    ? (raw as RegisterOrigin)
    : null;
}

export function patientCreateHref(
  prefillQuery: string | null,
  origin?: RegisterOrigin,
): string {
  const base = '/dashboard/patients/new';
  const params: string[] = [];
  if (prefillQuery !== null && prefillQuery.trim() !== '') {
    params.push(`q=${encodeURIComponent(prefillQuery.trim())}`);
  }
  if (origin !== undefined) {
    params.push(`from=${origin}`);
  }
  return params.length > 0 ? `${base}?${params.join('&')}` : base;
}

/**
 * The "Record payment" door back into billing entry with the identity +
 * visit link INTACT (PATIENT-MASTER-CONVERSION-AND-PAYMENT-LINK-COHERENCE-
 * V1 payment continuity): the URL params ARE the prefill carrier
 * (BillingEntryPage parses them once on mount), so the door re-supplies the
 * appointment link after any create/link round trip -- no storage, no
 * stale-link risk. appointmentId/clinicId are omitted when unknown; the
 * page then keeps its own clinic default and records an unlinked payment.
 */
export function recordPaymentHref(args: {
  patientId: string;
  clinicId: number | null;
  appointmentId: number | null;
}): string {
  const params = new URLSearchParams();
  params.set('patientId', args.patientId);
  if (args.clinicId !== null) params.set('clinicId', String(args.clinicId));
  if (args.appointmentId !== null) {
    params.set('appointmentId', String(args.appointmentId));
  }
  return `/dashboard/billing-entry?${params.toString()}`;
}

/**
 * The family-phone caption for a many-result phone search. Shown as
 * information, never as a warning — shared phones are a first-class
 * case (ADR-0013).
 */
export function sharedPhoneCaption(count: number): string {
  return (
    `${count} patients share this phone number — normal for families. ` +
    'Pick the right person below, or register a new patient on the same number.'
  );
}
